<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()

// État local des rôles simulés
const simulatedRoles = ref<Record<string, number>>({
  project: 4,
  company: 4,
  finance: 4,
  hr: 4,
  mission_order: 4,
  support: 4,
  wiki: 4,
  dev: 4,
})

// État pour la sélection d'utilisateur
const selectedUserId = ref<number | null>(null)
const users = ref<Array<{ id: number; firstname: string; surname: string; email: string; roles: Record<string, number>; cellule_id: number }>>([])

const isCollapsed = ref(true)
const saving = ref(false)

// Labels pour les modules
const moduleLabels: Record<string, string> = {
  project: 'Projets',
  company: 'Sociétés',
  finance: 'Finances',
  hr: 'RH',
  mission_order: 'OdM',
  support: 'Support',
  wiki: 'Wiki',
  dev: 'Dev',
}

// Labels pour les niveaux
const scopeLabels: Record<number, string> = {
  0: 'Aucun',
  1: 'Personnel',
  2: 'Cellule',
  3: 'Cellule Lead',
  4: 'Global',
}

// Couleurs pour les niveaux
const scopeColors: Record<number, string> = {
  0: 'bg-gray-200 text-gray-600',
  1: 'bg-blue-100 text-blue-700',
  2: 'bg-yellow-100 text-yellow-700',
  3: 'bg-orange-100 text-orange-700',
  4: 'bg-green-100 text-green-700',
}

// Helper pour accéder aux rôles de manière type-safe
function getRoleLevel(module: string): number {
  const roles = authStore.user?.roles
  if (!roles) return 0
  const value = (roles as unknown as Record<string, unknown>)[module]
  return typeof value === 'number' ? value : 0
}

// Résumé des rôles actuels
const rolesSummary = computed(() => {
  if (!authStore.user?.roles) return ''
  const roles = authStore.user.roles
  const parts: string[] = []
  if (roles.project > 0) parts.push(`P${roles.project}`)
  if (roles.finance > 0) parts.push(`F${roles.finance}`)
  if (roles.company > 0) parts.push(`C${roles.company}`)
  return parts.join(' ')
})

// Nom de l'utilisateur sélectionné
const selectedUserName = computed(() => {
  if (!selectedUserId.value) return null
  const user = users.value.find(u => u.id === selectedUserId.value)
  return user ? `${user.firstname} ${user.surname}` : null
})

// Charger tous les utilisateurs (pour le super admin)
async function loadUsers() {
  try {
    const response = await fetch('/api/users', {
      credentials: 'include',
    })
    const data = await response.json()
    if (data.success && data.data?.users) {
      users.value = data.data.users
    }
  } catch (e) {
    console.error('Erreur chargement utilisateurs:', e)
  }
}

// Quand un utilisateur est sélectionné, charger ses rôles automatiquement
watch(selectedUserId, (newUserId) => {
  if (!newUserId) return
  const user = users.value.find(u => u.id === newUserId)
  if (user && user.roles) {
    // Copier les rôles de l'utilisateur sélectionné
    simulatedRoles.value = { ...user.roles }
  }
})

// Activer la simulation
async function applySimulation() {
  saving.value = true
  try {
    const payload: Record<string, unknown> = { roles: simulatedRoles.value }
    if (selectedUserId.value !== null) {
      payload.user_id = selectedUserId.value
    }

    const response = await fetch('/api/dev/simulate-roles', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    })

    if (response.ok) {
      await authStore.refreshUser()
    }
  } catch (e) {
    console.error('Erreur simulation:', e)
  } finally {
    saving.value = false
  }
}

// Désactiver la simulation
async function clearSimulation() {
  saving.value = true
  try {
    const response = await fetch('/api/dev/simulate-roles', {
      method: 'DELETE',
      credentials: 'include',
    })

    if (response.ok) {
      await authStore.refreshUser()
    }
  } catch (e) {
    console.error('Erreur clear simulation:', e)
  } finally {
    saving.value = false
  }
}

// Charger les rôles simulés au démarrage
async function loadSimulatedRoles() {
  try {
    const response = await fetch('/api/dev/simulated-roles', {
      credentials: 'include',
    })
    const data = await response.json()

    if (data.success && data.data.active) {
      const roles = data.data.roles
      // Séparer le user_id des rôles
      if (roles._user_id !== undefined) {
        selectedUserId.value = roles._user_id
        delete roles._user_id
      }
      Object.assign(simulatedRoles.value, roles)
    }
  } catch (e) {
    console.error('Erreur chargement simulation:', e)
  }
}

// Barre visible pour un super admin dont la colonne role_dev en base est >= 1 (flag is_dev, non forcé par le super admin)
const canShowBar = computed(() => {
  if (!authStore.isSuperAdmin) return false
  return authStore.user?.roles?.is_dev === true
})

// Charger les rôles simulés et les utilisateurs quand la barre devient visible
watch(canShowBar, (visible) => {
  if (visible) {
    loadSimulatedRoles()
    loadUsers()
  }
}, { immediate: true })

// Réinitialiser l'utilisateur sélectionné quand la simulation est désactivée
watch(() => authStore.isSimulationActive, (isActive) => {
  if (!isActive) {
    selectedUserId.value = null
  }
})
</script>

<template>
  <!-- Barre visible pour super admin + dev -->
  <div
    v-if="canShowBar"
    class="fixed bottom-0 left-0 right-0 z-50 transition-all duration-300"
    :class="isCollapsed ? 'h-8' : 'h-auto'"
  >
    <!-- Barre réduite -->
    <div
      v-if="isCollapsed"
      class="h-8 flex items-center justify-between px-4 cursor-pointer"
      :class="authStore.isSimulationActive ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-300'"
      @click="isCollapsed = false"
    >
      <div class="flex items-center gap-2 text-xs">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span class="font-medium">Dev Tools</span>
        <span v-if="authStore.isSimulationActive" class="px-1.5 py-0.5 bg-white/20 rounded text-xs">
          SIMULATION: {{ rolesSummary }}
          <template v-if="selectedUserName"> | User: {{ selectedUserName }}</template>
        </span>
      </div>
      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
      </svg>
    </div>

    <!-- Barre développée -->
    <div
      v-else
      class="bg-gray-900 text-white shadow-lg"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-4 py-2 border-b border-gray-700 cursor-pointer"
        @click="isCollapsed = true"
      >
        <div class="flex items-center gap-2">
          <svg class="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span class="font-semibold">Simulateur de Rôles</span>
          <span
            v-if="authStore.isSimulationActive"
            class="px-2 py-0.5 bg-orange-500 rounded text-xs font-medium"
          >
            ACTIF
          </span>
        </div>
        <svg class="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <!-- Contenu -->
      <div class="p-4">
        <!-- Grille des modules -->
        <div class="grid grid-cols-8 gap-3 mb-4">
          <div
            v-for="(label, module) in moduleLabels"
            :key="module"
            class="text-center"
          >
            <label class="block text-xs text-gray-400 mb-1">{{ label }}</label>
            <select
              v-model="simulatedRoles[module]"
              class="w-full px-2 py-1.5 rounded text-sm text-gray-900 border-0 focus:ring-2 focus:ring-blue-500"
              :class="scopeColors[simulatedRoles[module]]"
            >
              <option v-for="(scopeLabel, level) in scopeLabels" :key="level" :value="Number(level)">
                {{ scopeLabel }}
              </option>
            </select>
          </div>
        </div>

        <!-- Sélecteur d'utilisateur (pour charger ses droits automatiquement) -->
        <div class="mb-4 flex items-center gap-3">
          <label class="text-xs text-gray-400 whitespace-nowrap">Simuler utilisateur :</label>
          <select
            v-model="selectedUserId"
            class="flex-1 max-w-md px-2 py-1.5 rounded text-sm text-gray-900 border-0 focus:ring-2 focus:ring-blue-500"
            :class="selectedUserId ? 'bg-purple-100 text-purple-700' : 'bg-gray-200 text-gray-600'"
          >
            <option :value="null">-- Choisir un utilisateur --</option>
            <option v-for="user in users" :key="user.id" :value="user.id">
              {{ user.firstname }} {{ user.surname }} ({{ user.email }})
            </option>
          </select>
          <span class="text-xs text-gray-500">(charge automatiquement ses droits)</span>
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-between">
          <div class="text-xs text-gray-500">
            Super Admin: {{ authStore.user?.username }}
          </div>
          <div class="flex items-center gap-2">
            <button
              v-if="authStore.isSimulationActive"
              @click="clearSimulation"
              :disabled="saving"
              class="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 rounded text-sm font-medium transition-colors disabled:opacity-50"
            >
              Désactiver
            </button>
            <button
              @click="applySimulation"
              :disabled="saving"
              class="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded text-sm font-medium transition-colors disabled:opacity-50"
            >
              {{ saving ? 'Enregistrement...' : 'Appliquer' }}
            </button>
          </div>
        </div>

        <!-- Rôles actuels (lecture seule) -->
        <div class="mt-3 pt-3 border-t border-gray-700">
          <div class="text-xs text-gray-500 mb-2">Rôles effectifs actuels :</div>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="(label, module) in moduleLabels"
              :key="module"
              class="px-2 py-1 rounded text-xs"
              :class="scopeColors[getRoleLevel(module as string)]"
            >
              {{ label }}: {{ scopeLabels[getRoleLevel(module as string)] }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
